import { useCallback, useEffect, useState } from 'react';
import axiosInstance from '../axiosInstance';
import { toTitleCase } from '../../utils/textCase';

const BASE = '/hotels';

// Guess a sensible icon key from a facility name (used as the default when no
// override is saved). Keys must exist in HotelDetails' FACILITY_ICON_OPTIONS.
const ICON_KEYWORDS = [
  ['pool', ['pool', 'swim']],
  ['spa', ['spa', 'wellness', 'sauna', 'massage', 'jacuzzi', 'hammam', 'thermal']],
  ['gym', ['gym', 'fitness', 'sport']],
  ['restaurant', ['restaurant', 'dining', 'buffet']],
  ['bar', ['bar', 'lounge', 'pub', 'cocktail']],
  ['breakfast', ['breakfast']],
  ['coffee', ['coffee', 'cafe', 'tea']],
  ['parking', ['parking', 'garage', 'valet']],
  ['shuttle', ['shuttle', 'transfer', 'airport']],
  ['wifi', ['wifi', 'wi-fi', 'internet', 'wireless']],
  ['ac', ['air condition', 'a/c', 'climate']],
  ['heating', ['heating', 'heater']],
  ['tv', ['tv', 'television']],
  ['minibar', ['minibar', 'mini bar', 'mini-bar']],
  ['safe', ['safe', 'safety deposit', 'safe box']],
  ['elevator', ['elevator', 'lift']],
  ['laundry', ['laundry', 'dry clean', 'washing']],
  ['reception24', ['24-hour', '24 hour', '24h', 'reception', 'front desk']],
  ['concierge', ['concierge']],
  ['room-service', ['room service']],
  ['luggage', ['luggage', 'baggage']],
  ['pet', ['pet', 'dog', 'animal']],
  ['beach', ['beach', 'seafront', 'sea view']],
  ['garden', ['garden', 'terrace', 'patio']],
  ['kids', ['kid', 'child', 'family', 'playground']],
  ['tennis', ['tennis']],
  ['golf', ['golf']],
  ['bike', ['bike', 'bicycle', 'cycling']],
  ['casino', ['casino']],
  ['business', ['business', 'meeting', 'conference', 'banquet']],
  ['shop', ['shop', 'store', 'boutique', 'market']],
  ['currency', ['currency', 'exchange', 'atm', 'cash']],
  ['wheelchair', ['wheelchair', 'accessible', 'disabled']],
  ['nonsmoking', ['non-smoking', 'no smoking', 'non smoking']],
];

export const guessFacilityIcon = (name = '') => {
  const n = String(name).toLowerCase();
  for (const [icon, words] of ICON_KEYWORDS) {
    if (words.some((w) => n.includes(w))) return icon;
  }
  return 'check';
};

const STAR_TO_CATEGORY = {
  1: '1EST',
  2: '2EST',
  3: '3EST',
  4: '4EST',
  5: '5EST',
};

const IMAGE_TYPE_TO_GALLERY_KEY = {
  RES: 'exterior',
  GEN: 'general',
  HAB: 'rooms',
  PIS: 'pool',
  REST: 'restaurant',
  BAR: 'bar',
  SPA: 'spa',
  GIM: 'gym',
  SAL: 'meeting',
  JAR: 'garden',
  LOB: 'lobby',
  PLY: 'beach',
  COM: 'common',
  TER: 'terrace',
};

const normalizeGalleryKey = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'general';

const getPrimaryPhone = (phones = []) => {
  if (!Array.isArray(phones) || phones.length === 0) return '';
  const bookingPhone = phones.find((p) => p?.type === 'PHONEBOOKING');
  return bookingPhone?.number || phones[0]?.number || '';
};

export const mapApiHotelToUi = (apiHotel) => {
  if (!apiHotel) return null;

  return {
    id: apiHotel.id,
    hotelCode: apiHotel.hotelCode ?? null,
    hotelName: apiHotel.name ?? '',
    // The geo city name ("Istanbul"), which is already correctly cased and spelled. Falls
    // back to the supplier's own text — title-cased, since that arrives as "BANGKOK" as
    // often as "Istanbul" — for the rare hotel whose destination is missing from geo.
    city: apiHotel.cityName || toTitleCase(apiHotel.city),
    cityCode: apiHotel.cityCode ?? '',
    // Raw supplier text, kept for reference; never displayed in the list.
    supplierCity: apiHotel.city ?? '',
    country: apiHotel.countryCode ?? '',
    // Resolved ENG zone name when the geo tables know this (destination, zoneCode)
    // pair; falls back to the raw code so the list never shows an unlabeled blank.
    zone: apiHotel.zoneName || (apiHotel.zoneCode != null ? `Zone ${apiHotel.zoneCode}` : ''),
    primaryImage: apiHotel.primaryImage ?? null,
    address: apiHotel.address ?? '',
    // The "Stars" export column. Prefer the parsed star rating so 5LUX and a bare "3" count as
    // the 5 and 3 stars they are — apiHotel.stars only knows the 1EST–5EST codes. Stays 0 for
    // apartments and unclassified properties: neither has a star rating to report.
    starRating: apiHotel.rating?.kind === 'star'
      ? apiHotel.rating.value
      : (Number.isFinite(apiHotel.stars) ? apiHotel.stars : 0),
    // The property's official classification. kind is 'star' for hotels, 'key' for apartments
    // (Hotelbeds rates those in llaves), and NULL when the property has no classification at
    // all — a guesthouse, boutique or villa. Kept separate from starRating, which only knows
    // the 1EST–5EST codes and so reads 0 for a 5LUX hotel and every apartment alike.
    ratingKind: apiHotel.rating?.kind ?? null,
    ratingValue: apiHotel.rating?.value ?? null,
    // Stored TripAdvisor guest rating on the SUNSKY /10 scale (or null).
    guestRating: apiHotel.guestRating
      ? { score10: Number(apiHotel.guestRating.score10), count: Number(apiHotel.guestRating.count) || 0 }
      : null,
    contactPerson: '',
    contactEmail: apiHotel.email ?? '',
    contactPhone: apiHotel.phone ?? '',
    status: apiHotel.active ? 'active' : 'inactive',
  };
};

export const mapUiCreateHotelToApi = (uiHotel) => {
  const parsedStars = Number(uiHotel.starRating || 0);
  const clean = (v) => { const s = String(v ?? '').trim(); return s || null; };
  const num = (v) => (v !== '' && v != null && !Number.isNaN(Number(v)) ? Number(v) : null);

  return {
    name: String(uiHotel.hotelName ?? '').trim(),
    city: clean(uiHotel.city),
    countryCode: clean(uiHotel.country),
    stateCode: clean(uiHotel.stateCode),
    address: clean(uiHotel.address),
    postalCode: clean(uiHotel.postalCode),
    categoryCode: STAR_TO_CATEGORY[parsedStars] || null,
    accommodationTypeCode: clean(uiHotel.accommodationTypeCode),
    chainCode: clean(uiHotel.chainCode),
    destinationCode: clean(uiHotel.destinationCode),
    zoneCode: num(uiHotel.zoneCode),
    latitude: num(uiHotel.latitude),
    longitude: num(uiHotel.longitude),
    email: clean(uiHotel.contactEmail),
    contactPerson: clean(uiHotel.contactPerson),
    phone: clean(uiHotel.contactPhone),
    web: clean(uiHotel.web),
    description: String(uiHotel.description ?? '').trim(),
    active: uiHotel.status === 'active',
  };
};

export const mapApiHotelDetailsToUi = (apiHotel) => {
  if (!apiHotel) return null;

  const images = Array.isArray(apiHotel.images) ? apiHotel.images : [];
  const primaryImage = images.find((img) => img?.url)?.url || '';
  const phones = Array.isArray(apiHotel.phones) ? apiHotel.phones : [];
  const sourceRooms = Array.isArray(apiHotel.rooms) ? apiHotel.rooms : [];
  const sourceFacilities = Array.isArray(apiHotel.facilities) ? apiHotel.facilities : [];
  const sourceHighlights = Array.isArray(apiHotel.popularFacilities) ? apiHotel.popularFacilities : [];
  const rateComments = Array.isArray(apiHotel.rateComments) ? apiHotel.rateComments : [];

  const imageGallery = {};
  images.forEach((img) => {
    if (!img?.url) return;
    const fromCode = IMAGE_TYPE_TO_GALLERY_KEY[img.imageTypeCode];
    const fromLabel = normalizeGalleryKey(img.imageTypeCode || img.roomType || 'general');
    const key = fromCode || fromLabel;
    if (!imageGallery[key]) imageGallery[key] = [];
    imageGallery[key].push(img.url);
  });
  if (!Object.keys(imageGallery).length) {
    imageGallery.general = primaryImage ? [primaryImage] : [];
  }

  const mappedRooms = sourceRooms.map((room, index) => {
    const roomImages = Array.isArray(room.images) ? room.images.map((img) => img?.url).filter(Boolean) : [];
    const roomFacilities = Array.isArray(room.facilities) ? room.facilities : [];

    return {
      id: room.id ?? `${room.roomCode || 'room'}-${index}`,
      roomType: room.name || room.roomType || room.roomCode || 'Room',
      description: room.description || '',
      size: '',
      maxOccupancy: Number(room?.capacity?.maxPax) || Number(room?.maxPax) || 2,
      bedConfiguration: room.characteristicCode || '',
      mainImage: roomImages[0] || '',
      images: roomImages,
      amenities: roomFacilities
        .map((f) => f.facilityName || (f.facilityCode ? `Facility ${f.facilityCode}` : ''))
        .filter(Boolean)
        .slice(0, 20),
    };
  });

  return {
    id: apiHotel.id,
    hotelCode: apiHotel.hotelCode ?? null,
    hotelName: apiHotel.name || '',
    sunskyMarketingName: apiHotel.name || '',
    starRating: Number.isFinite(apiHotel.stars) ? apiHotel.stars : 0,
    hotelType: apiHotel.categoryGroupCode || apiHotel.starLabel || '',
    status: apiHotel.active ? 'active' : 'inactive',
    contactPerson: '',
    contactEmail: apiHotel.email || '',
    contactPhone: getPrimaryPhone(phones),
    website: apiHotel.web || '',
    country: apiHotel.countryCode || '',
    city: apiHotel.city || '',
    destination: apiHotel.destinationCode || '',
    // Zone is chosen from the destination's real zones. zoneCode is the stored identity;
    // `zone` is its resolved ENG name (for display) derived from that code.
    zoneCode: apiHotel.zoneCode ?? null,
    zone: apiHotel.zoneName || (apiHotel.zoneCode != null ? `Zone ${apiHotel.zoneCode}` : ''),
    address: apiHotel.address || '',
    zipCode: apiHotel.postalCode || '',
    latitude: apiHotel.latitude ?? '',
    longitude: apiHotel.longitude ?? '',
    mainImage: primaryImage,
    hotelDescription: apiHotel.description || '',
    locationDescription: apiHotel.description || '',
    highlights: sourceHighlights
      .map((f) => f.facilityName || (f.facilityCode ? `Facility ${f.facilityCode}` : ''))
      .filter(Boolean),
    facilities: sourceFacilities.map((f) => {
      const name = f.facilityName || (f.facilityCode ? `Facility ${f.facilityCode}` : '');
      return {
        // Identity for persisting the icon override (key = "group-code").
        facilityCode: f.facilityCode ?? null,
        facilityGroupCode: f.facilityGroupCode ?? null,
        // Saved override wins; otherwise guess a sensible icon from the name.
        icon: f.icon || guessFacilityIcon(name),
        name,
      };
    }),
    imageGallery,
    rooms: mappedRooms,
    notes: rateComments.map((comment) => comment?.commentText).filter(Boolean).join('\n'),
    disclaimers: '',
  };
};

export const mapUiHotelDetailsToUpdatePayload = (uiHotel) => {
  const parsedStars = Number(uiHotel?.starRating || 0);
  const parseNumberOrNull = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  return {
    name: String(uiHotel?.hotelName || '').trim(),
    categoryCode: STAR_TO_CATEGORY[parsedStars] || null,
    active: uiHotel?.status === 'active',
    city: String(uiHotel?.city || '').trim() || null,
    countryCode: String(uiHotel?.country || '').trim() || null,
    address: String(uiHotel?.address || '').trim() || null,
    postalCode: String(uiHotel?.zipCode || '').trim() || null,
    email: String(uiHotel?.contactEmail || '').trim() || null,
    web: String(uiHotel?.website || '').trim() || null,
    latitude: parseNumberOrNull(uiHotel?.latitude),
    longitude: parseNumberOrNull(uiHotel?.longitude),
    destinationCode: String(uiHotel?.destination || '').trim() || null,
    phone: String(uiHotel?.contactPhone || '').trim() || null,
    description: String(uiHotel?.hotelDescription || '').trim(),
    // Zone is picked from the destination's real zones, so we persist the selected code and
    // let the name derive from it server-side.
    zoneCode: parseNumberOrNull(uiHotel?.zoneCode),
  };
};

export const getHotels = (params = {}) =>
  axiosInstance.get(BASE, { params }).then((res) => res.data);

// Country list for the filter rarely changes, so cache the in-flight promise.
// This collapses React.StrictMode's double mount-effect into ONE network request
// and makes remounts instant. Pass { force: true } to refresh (e.g. after adding a
// hotel in a new country).
let _hotelCountriesPromise = null;
export const getHotelCountries = ({ force = false } = {}) => {
  if (force) _hotelCountriesPromise = null;
  if (!_hotelCountriesPromise) {
    _hotelCountriesPromise = axiosInstance
      .get(`${BASE}/meta/countries`)
      .then((res) => res.data)
      .catch((err) => { _hotelCountriesPromise = null; throw err; }); // allow retry
  }
  return _hotelCountriesPromise;
};

// Cities come from geo data (destinations), keyed by code. Zones are the districts of one
// city and need that city's code — see the controller for why the pair is required.
export const getHotelCities = (params = {}) =>
  axiosInstance.get(`${BASE}/meta/cities`, { params }).then((res) => res.data);

// All assignable zones for a destination (geo table), for the hotel-detail zone dropdown.
export const getDestinationZones = (destinationCode) =>
  axiosInstance
    .get(`${BASE}/meta/destination-zones`, { params: { destinationCode } })
    .then((res) => res.data);

// Pull the latest Hotelbeds content for a scope (country / destination-city / zone) and refresh
// the hotels we hold there. Content only — never touches prices or on/off state.
export const refreshHotelContent = (scope) =>
  axiosInstance.post(`${BASE}/refresh-content`, scope).then((res) => res.data);

export const getHotelZones = (params = {}) =>
  axiosInstance.get(`${BASE}/meta/zones`, { params }).then((res) => res.data);

export const createHotel = (payload) => axiosInstance.post(BASE, payload).then((res) => res.data);

// Upload one hotel image (multipart, field: image) → OVH storage → hotelImages.
export const uploadHotelImage = (hotelId, file, imageTypeCode = null) => {
  const fd = new FormData();
  fd.append('image', file);
  // The gallery category it was added under (GEN, HAB, PIS…); the server defaults to GEN.
  if (imageTypeCode) fd.append('imageTypeCode', imageTypeCode);
  return axiosInstance
    .post(`${BASE}/${hotelId}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    .then((res) => res.data);
};

// Remove a gallery image. A Hotelbeds photo stays removed: future syncs do not add it back.
export const deleteHotelImage = (hotelId, imageUrl) =>
  axiosInstance
    .delete(`${BASE}/${hotelId}/images`, { data: { imageUrl } })
    .then((res) => res.data);

/** The Hotelbeds image type code of a gallery category key ('pool' → 'PIS'); an unknown key is its own code. */
export const galleryKeyToImageType = (key) => {
  const k = String(key || '').toLowerCase();
  const hit = Object.entries(IMAGE_TYPE_TO_GALLERY_KEY).find(([, v]) => v === k);
  return hit ? hit[0] : (k ? k.toUpperCase().slice(0, 10) : 'GEN');
};

// Make an existing gallery image the master/primary image (sorts first everywhere).
export const setHotelPrimaryImage = (hotelId, imageUrl) =>
  axiosInstance
    .patch(`${BASE}/${hotelId}/images/primary`, { imageUrl })
    .then((res) => res.data);

export const getHotelById = (hotelId) => axiosInstance.get(`${BASE}/${hotelId}`).then((res) => res.data);

export const getHotelOverviewById = (hotelId) =>
  axiosInstance.get(`${BASE}/${hotelId}/overview`).then((res) => res.data);

export const updateHotelById = (hotelId, payload) =>
  axiosInstance.put(`${BASE}/${hotelId}`, payload).then((res) => res.data);

// Persist per-hotel facility icon overrides. `icons` is a map keyed
// "{facilityGroupCode}-{facilityCode}" → icon key (e.g. { "70-260": "pool" }).
export const updateHotelFacilityIcons = (hotelId, icons) =>
  axiosInstance.put(`${BASE}/${hotelId}/facilities/icons`, { icons }).then((res) => res.data);

export const toggleHotelStatus = (hotelId, active) =>
  axiosInstance.patch(`${BASE}/${hotelId}/status`, { active }).then((res) => res.data);

// Rating switch-off rule (the "apply rating rule" button): preview the counts, then apply.
export const getRatingSwitchOffPreview = (params = {}) =>
  axiosInstance.get(`${BASE}/rating-switch-off/preview`, { params }).then((res) => res.data);
export const applyRatingSwitchOff = (payload = {}) =>
  axiosInstance.post(`${BASE}/rating-switch-off`, payload).then((res) => res.data);

export const useGetHotels = (params = {}) => {
  const {
    page = 1,
    limit = 20,
    search = '',
    status = 'all',
    country = 'all',
    city = 'all',
    zone = 'all',
    category = 'all',
    rating = 'all',
    sortBy = 'name',
    sortOrder = 'ASC',
  } = params;

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHotels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = { page, limit, sortBy, sortOrder };
      if (search) queryParams.search = search;
      if (status && status !== 'all') queryParams.status = status;
      if (country && country !== 'all') queryParams.country = country;
      if (city && city !== 'all') queryParams.city = city;
      if (zone && zone !== 'all') queryParams.zone = zone;
      if (category && category !== 'all') queryParams.category = category;
      if (rating && rating !== 'all') queryParams.rating = rating;

      const res = await getHotels(queryParams);

      if (res.success) {
        setData((res.data || []).map(mapApiHotelToUi));
        if (res.pagination) {
          setPagination(res.pagination);
        }
      } else {
        setError(res.message || 'Failed to load hotels');
        setData([]);
      }
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to load hotels';
      setError(message);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, status, country, city, zone, category, rating, sortBy, sortOrder]);

  useEffect(() => {
    fetchHotels();
  }, [fetchHotels]);

  const refetch = useCallback(() => {
    fetchHotels();
  }, [fetchHotels]);

  return { data, pagination, loading, error, refetch };
};
